import {
  Stack,
  Box,
  Typography,
  Link,
  Grid,
  CardMedia,
} from "@mui/material";
import Button from "components/Button";
import useTranslate from "hooks/useTranslate";
import { useState, useEffect, useContext } from "react";
import amnPosApi from "api/amnPosApi";
import useAmnNavigate from "hooks/useAmnNavigate";
import reportHttpErrors from "helpers/reportHttpErrors";
import { AppType } from "constants/AppType";
import { useAppDispatch } from "app/hooks";
import useShift from "amnPos/shiftsAndExternals/shift/useShift";
import { signoutAsync } from "adminPanel/user/userSlice";
import { getShiftInfoAsync } from "amnPos/slices/ShiftInfoSlice";
import FilterListBox, {
  IFilterListBoxItem,
} from "amnPos/generalComponent/FilterListBox";
import useIsSmUp from "hooks/useIsSmUp";
import i18n from "localization/i18n";
import logo from "/images/SignLogo.svg";
import shap1 from "/images/shap1.svg";
import shap2 from "/images/shap2.svg";
import shap3 from "/images/shap3.svg";
import illustration from "/images/illustration & font.svg";
import { useThemeColors } from "theme";
import LanguageSelector from "components/general/LanguageSelector";
import { Snackbar } from "components/general/Snackbar";
import SignLogo from "components/icons/SignLogo";
import useEgyptPosStoreGuard from "adminPanel/stores/useEgyptPosStoreGuard";
import { store } from "app/store";
import { TaxesSystems } from "constants/taxesSystems";
import { TaxSystem } from "adminPanel/settings/settingTaxes/ITaxSystem";
import settingsAPI from "adminPanel/settings/settingsAPI";
import MessageDialogContext from "context/MessageDialogContext";

export enum POSType {
  POS,
  Restaurants,
}
export interface IPosAutoComplete {
  id: string;
  name: string;
  posType: POSType;
}

export default function UserHomePage() {
  const { translate } = useTranslate("pos.userHome");
  const { translate: taxTranslate } = useTranslate("admin.taxes");
  const { amnNavigate } = useAmnNavigate();
  const { ensureStoreIsValid } = useEgyptPosStoreGuard();
  const colors = useThemeColors();
  const [posList, setPosList] = useState<IPosAutoComplete[]>([]);
  const [pos, setPos] = useState<IPosAutoComplete | null>(null);
  const dispatch = useAppDispatch();
  const { ValidateShift } = useShift();
  const messageDialog = useContext(MessageDialogContext);
  function HandlePosTypeNavigation(posType: POSType): void {
    switch (posType) {
      case POSType.Restaurants:
        amnNavigate("/restaurants", AppType.Organizations, {
          replace: true,
        });
        return;
      default:
        amnNavigate("/pos", AppType.Organizations, { replace: true });
        return;
    }
  }
  useEffect(() => {
    amnPosApi
      .GetPosList()
      .then((res) => setPosList(res.data))
      .catch((e) => {
        reportHttpErrors(e.response, Snackbar);
      });
  }, [Snackbar]);

  const handleClick = async () => {
    if (!pos?.id) return;

    if (!(await ensureStoreIsValid(pos.id))) return;

    if (
      store.getState().organizationSettings.taxState.system ===
        TaxSystem.Syria ||
      store.getState().organizationSettings.taxState.system === TaxSystem.Egypt
    ) {
      const data = (await settingsAPI.GetBusinessInfo()).data;
      if (!Boolean(data?.businessName?.trim()) || !Boolean(data.address)) {
        messageDialog.setActionCloseMessageInfo(
          taxTranslate("businessInfoRequiredTitle"),
          taxTranslate("businessInfoRequiredBody").replace(
            "{{taxSystem}}",
            taxTranslate(
              TaxesSystems.find(
                (c) =>
                  c.value ===
                  store.getState().organizationSettings.taxState.system,
              )!.name,
            ),
          ),
          "close",
        );
        return;
      }
    }

    localStorage.setItem("posId", pos.id);
    localStorage.setItem("posType", String(pos.posType ?? POSType.POS));
    dispatch(getShiftInfoAsync({ posId: pos.id })).then(() => {
      ValidateShift(pos.id, () => {
        amnNavigate(
          `/${
            (pos.posType ?? POSType.POS).toString() ===
            POSType.Restaurants.toString()
              ? "restaurants"
              : "pos"
          }`,
          AppType.Organizations,
          {
            replace: true,
          },
        );
      });
    });
  };

  const handleSignOut = () => {
    dispatch(signoutAsync());
  };
  const isSmUp = useIsSmUp();
  const style = {
    container: {
      height: "100vh",
    },
    cardHeader: {
      alignItems: "center",
    },
    containerLogin: {
      display: "flex",
      justifyContent: "center",
      right: 0,
    },
    containerImage: {
      backgroundImage: "url('/images/BG.svg')",
      backgroundSize: "cover",
      backgroundAttachment: "fixed",
      position: "relative",
    },
    shap1: {
      width: "7vw",
      right: 0,
      position: "absolute",
      objectFit: "contain",
      transform: i18n.language == "ar" ? "scaleX(-1)" : "",
    },
    shap2: {
      width: "13vw",
      bottom: 0,
      position: "absolute",
      objectFit: "contain",
      transform: i18n.language == "ar" ? "scaleX(-1)" : "",
    },
    logo: {
      width: "8vw",
      top: "2%",
      left: "3%",
      position: "absolute",
      objectFit: "contain",
    },
    shap3: {
      width: "13vw",
      right: 0,
      bottom: 0,
      position: "absolute",
      objectFit: "contain",
      transform: i18n.language == "ar" ? "scaleX(-1)" : "",
    },
    illustration: {
      top: "23%",
      position: "absolute",
      objectFit: "cover",
    },
    containerMobileLog: {
      backgroundImage: "url('/images/BGMobile.svg')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      justifyContent: "center",
      alignItems: "center",
      paddingX: 2,
      height: "85dvh",
    },
    cardContent: {
      width: isSmUp ? "35vw" : "100dvw",
      justifyContent: "center",
    },
    Link: {
      textDecoration: "none",
      color: colors.text.link,
      fontWeight: 500,
    },
    langSelect: {
      mobileView: {
        color: colors.text.link,
        width: "100%",
      },
      default: {
        top: 22,
        right: i18n.language == "en" ? 5 : "unset",
        left: i18n.language == "ar" ? 5 : "unset",
        color: "white",
      },
    },
  };
  const Images = (
    <Stack
      sx={{
        position: "relative",
        height: "100%",
        width: "50%"
      }}>
      <Stack
        sx={{
          position: "fixed",
          height: "100%",
          width: "50%"
        }}>
        <CardMedia component={"img"} image={logo} sx={style.logo} />
        <CardMedia component={"img"} image={shap1} sx={style.shap1} />
        <CardMedia component={"img"} image={shap2} sx={style.shap2} />
        <CardMedia component={"img"} image={shap3} sx={style.shap3} />
        <CardMedia
          component={"img"}
          image={illustration}
          sx={style.illustration}
        ></CardMedia>
      </Stack>
    </Stack>
  );
  const cardContent = (
    <Stack style={style.cardContent}>
      <Stack
        sx={{
          alignItems: "center",
          textAlign: "center"
        }}>
        <Box
          sx={{
            width: "auto",
            height: "auto"
          }}>
          <SignLogo width={"210px"} height={"190px"} />
        </Box>
        <Typography
          sx={{
            mt: 2,
            mb: "12px",
            fontSize: "28px",
            fontWeight: 600
          }}>
          {translate("choosePOS")}
        </Typography>

        <Typography
          sx={{
            mb: 5,
            fontSize: "16px",
            color: "#7A8B98",
            fontWeight: 300
          }}>
          {translate("choosePosWelcome")}
        </Typography>
      </Stack>
      {posList && (
        <FilterListBox
          data={posList}
          onSelectedItemChanged={(item: IFilterListBoxItem | null) =>
            setPos(posList.filter((pos) => pos.id === item?.id)[0])
          }
        />
      )}
      <Stack
        sx={{
          alignItems: "center",
          width: "100%"
        }}>
        <Button
          data-testid="user-home-enter-pos"
          fullWidth
          sx={{
            height: "48px",
            fontWeight: "350",
          }}
          variant="confirmAction"
          disabled={pos === null}
          onClick={handleClick}
        >
          {translate("ok")}
        </Button>
        <Typography
          sx={{
            mt: "12px",
            mb: "0px",
            fontSize: "14px",
            color: "#7A8B98",
            fontWeight: 400
          }}>
          <Link component="button" onClick={handleSignOut} style={style.Link}>
            {translate("signOut")}
          </Link>
        </Typography>
        <Stack
          sx={isSmUp ? style.langSelect.mobileView : style.langSelect.default}
        >
          {/* <LanguageSelector /> */}
        </Stack>
      </Stack>
    </Stack>
  );

  return isSmUp ? (
    <Grid container sx={style.container}>
      <Grid size={6} sx={style.containerImage}>
        {Images}
      </Grid>
      <Grid size={6} sx={style.containerLogin}>
        {cardContent}
      </Grid>
    </Grid>
  ) : (
    <Grid container sx={style.containerMobileLog}>
      {cardContent}
    </Grid>
  );
}
